import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { api, priceFormat } = require('@cashew/utils');

interface ProofOfPaymentResponse {
  status: string;
  message: string;
  data: {
    totalBalanceAmount: number;
    uploadedFilePath: string;
    settlementIds: string;
  };
}

interface Props {
  isVisible: boolean;
  onClose: () => void;
  settlementId: string | number;
}

const ProofOfPaymentModal: React.FC<Props> = ({
  isVisible,
  onClose,
  settlementId,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [proofData, setProofData] = useState<ProofOfPaymentResponse['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchProofOfPaymentData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: ProofOfPaymentResponse = await api.get({
        endpoint: `settlements/review-proof/${settlementId}`,
      });
      setProofData(response.data);
    } catch (err) {
      console.error('Failed to fetch proof of payment data:', err);
      setError('Failed to load proof of payment details');
    } finally {
      setIsLoading(false);
    }
  }, [settlementId]);

  useEffect(() => {
    if (isVisible && settlementId) {
      fetchProofOfPaymentData();
    }
  }, [isVisible, settlementId, fetchProofOfPaymentData]);

  const handleDownloadProof = () => {
    if (proofData?.uploadedFilePath) {
      const a = document.createElement('a');
      a.href = proofData.uploadedFilePath;
      a.target = '_blank';
      a.download = `proof-of-payment-${settlementId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Parse settlementIds string into array (could be comma-separated)
  const getSettlementIdsList = (): string[] => {
    if (!proofData?.settlementIds) return [];
    return proofData.settlementIds.split(',').map((id) => id.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className="fixed inset-0 bg-black h-full bg-opacity-50 flex justify-center items-center z-[1000]"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="bg-white rounded-lg w-[90%] max-w-[700px] max-h-[80vh] overflow-hidden shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="flex justify-between items-start px-6 py-4">
          <h2 className="m-0 text-lg font-semibold text-black">
            {t('settlements.proofOfPayment.title')}
          </h2>
          <button
            type="button"
            className="bg-transparent border-none cursor-pointer p-1 text-gray-600 hover:text-gray-800 transition-colors"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <title>Close</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1">
          {isLoading && (
            <div className="flex justify-center items-center min-h-[150px]">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-800 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center text-red-500 py-5 text-sm">{error}</div>
          )}

          {!isLoading && !error && proofData && (
            <>
              <div className="text-gray-700 flex text-sm mb-1">
                The following proof was submitted for the following settlements
                totaling{' '}
                <span className="font-bold px-1">
                  {priceFormat({
                    currency: 'AED',
                    amount: Math.abs(proofData.totalBalanceAmount || 0),
                  })}
                </span>
                :
              </div>

              {getSettlementIdsList().length > 0 && (
                <div className="mb-1 flex flex-col justify-content-start align-items-start">
                  {getSettlementIdsList().map((id) => (
                    <div key={id} className="text-gray-600 text-sm py-1">
                      {id}
                    </div>
                  ))}
                </div>
              )}

              {proofData.uploadedFilePath && (
                <div className="mt-4 flex">
                  <button
                    type="button"
                    className="text-purple-600 hover:text-purple-800 underline text-sm cursor-pointer bg-transparent border-none p-0"
                    onClick={handleDownloadProof}
                  >
                    Download Proof Document
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProofOfPaymentModal;
